import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { createSelector } from '@reduxjs/toolkit';

import {
  ColumnSettingsType,
  TableSettingsType,
} from 'src/constants/componentTypes/tableTypes';
import {
  BaseEntity,
  Client as ClientType,
  Company as CompanyType,
  User,
  UserRolesTypesUnMapped,
} from 'src/constants/dataTypes';
import {
  MultiSelectOption,
  SelectOption,
} from 'src/legacy/components/Select/types';
import { AccessLevel } from 'src/legacy/components/UsersTable/useClientAccessForm';
import { RootState } from 'src/store/reduxTypes';
import { selectCurrentUser } from 'src/store/storeUtils';
import { arrayToMap } from 'src/utils/array';

export type Client = ClientType;
export type Company = CompanyType;

export interface CompanyFormData {
  companyId?: string;
  companyName?: string;
  isPlaceholder?: boolean;
  customerId?: string;
  leadUserID?: string;
  avatarImageURL?: string;
  customFields?: Record<string, string | string[]>;
  additionalFields?: {};
}

export interface ClientFormData {
  userId?: string;
  role?: string;
  companyId?: string;
  companyName?: string;
  avatarImageUrl?: string;
  cognitoEmail?: string;
  cognitoFirstName?: string;
  cognitoLastName?: string;
  sendInvite?: boolean;
  customFields?: Record<string, any>;
  noPasswordSet?: boolean;
  externalAuthProviders?: Array<CognitoHostedUIIdentityProvider>;
  additionalFields?: {
    formsAssociated?: string[];
    assignedToCompanies?: Map<string, boolean>;
    leadOfCompanies?: Map<string, boolean>;
  };
  isPlaceholder?: boolean;
  password?: string;
  mfaPreference?: string;
  notificationsMuted?: boolean;
}

export interface CompanyCellData {
  id: string;
  name: string;
  avatarUrl: string;
  isPlaceholder: boolean;
  fallbackColor: string;
}

export interface ClientTableRowModel {
  userId: string;
  ref?: string;
  name: string;
  email: string;
  userName: string;
  fallbackColor: string;
  picture: string;
  clientFullData?: Client;
  company: CompanyCellData[] | string;
  role?: string;
  creationDate: string;
  status: string;
  userRoles: Array<UserRolesTypesUnMapped>;
  clientAccess?: AccessLevel;
  companyId?: string;
  companyAccessList?: User['companyAccessList'];
  invitedBy?: {
    givenName: string;
    familyName: string;
    avatarUrl: string;
    email: string;
    useAvatar?: boolean;
    fallbackColor: string;
  };
}

export interface AssigneeDropDownOptions extends SelectOption {
  role: string;
}

export enum CustomFieldEntityType {
  CLIENT = 'client',
  COMPANY = 'company',
}

export interface CustomFieldEntity extends BaseEntity {
  additionalFields: Record<string, CustomFieldOption>;
}

export interface ClientsState {
  clients: Client[];
  activeClients: Client[];
  activeCompanies: Company[];
  archivedCompanies: Company[];
  companies: Company[];
  companyDataMap: Record<string, Record<string, string>>;
  isLoading: boolean;
  loadedClients: boolean;
  isInviting: boolean;
  invitedClient: boolean;
  isCreating: boolean;
  isRemoving: boolean;
  isUpdating: boolean;
  updatedClient: boolean;
  error: string;
  crmTableSettings: TableSettingsType;
  updatingClientsIds: Record<string, boolean>;
  lastVisitedTab: string;
}
export const AssistantUserID = 'global_assistant';
export const AssemblyAssistant: Client = {
  entityStatus: 'ENABLED',
  getstreamId: AssistantUserID,
  metadata: {},
  owner: null,
  fields: {
    givenName: 'Assembly',
    familyName: 'Assistant',
    email: 'assembly-assistant@assembly.com',
    avatarImageUrl: 'https://cdn.assembly.com/images/AssistantAvatar.png',
    fallbackColor: '',
    email_verified: '',
    phone_number: '',
    userStatus: '',
    getstreamId: AssistantUserID,
    cognitoCreated: '',
    roles: [],
  },
  additionalFields: {},
  id: AssistantUserID,
  ownerId: '',
  identityId: '',
  createdDate: '',
  updatedDate: '',
  companyId: '',
};

export type CustomFieldType =
  | 'phoneNumber'
  | 'email'
  | 'url'
  | 'text'
  | 'number'
  | 'address'
  | 'multiSelect';

// Custom field operation types for tracking changes
export type CustomFieldOperationType =
  | 'createOption'
  | 'updateOption'
  | 'deleteOption'
  | 'colorChange'
  | 'reorder'
  | 'createField'
  | 'updateField'
  | 'deleteField';

// Base custom field structure
export interface CustomFieldOption<
  Option extends MultiSelectOption = MultiSelectOption,
> {
  id: string;
  key: string;
  order: number;
  name: string;
  type: CustomFieldType;
  options?: Array<Option>;
}

// Address custom field values
export interface AddressCustomFieldValues {
  country?: string;
  region?: string;
  postalCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  fullAddress: string;
}

// Address custom field structure
export interface AddressCustomField
  extends CustomFieldOption,
    AddressCustomFieldValues {}

// Multi select custom field structure
export interface MultiSelectCustomField extends CustomFieldOption {
  options: Array<MultiSelectOption>;
}

export function isMultiSelectCustomField(
  field: CustomFieldOption,
): field is MultiSelectCustomField {
  return (
    field &&
    field.type === 'multiSelect' &&
    'options' in field &&
    Array.isArray(field.options)
  );
}

export function isAddressCustomField(
  field: CustomFieldOption,
): field is AddressCustomField {
  return field && field.type === 'address';
}

export const LOAD_CLIENTS_SUCCESS = 'LOAD_CLIENTS_SUCCESS';
export const LOAD_COMPANIES_REQUEST = 'LOADING_COMPANIES';
export const LOAD_COMPANIES_SUCCESS = 'LOAD_COMPANIES_SUCCESS';
export const LOAD_COMPANIES_FAILURE = 'LOAD_COMPANIES_FAILURE';
export const ADD_CLIENT_REQUEST = 'ADDING_CLIENT';
export const ADD_CLIENT_SUCCESS = 'ADD_CLIENT_SUCCESS';
export const ADD_CLIENT_FAILURE = 'ADD_CLIENT_FAILURE';
export const INVITE_CLIENT_REQUEST = 'INVITING_CLIENT';
export const INVITE_CLIENT_SUCCESS = 'INVITE_CLIENT_SUCCESS';
export const INVITE_CLIENT_FAILURE = 'INVITE_CLIENT_FAILURE';
export const UPDATE_CLIENT_REQUEST = 'UPDATE_CLIENT_REQUEST';
export const UPDATE_CLIENTS_SUCCESS = 'UPDATE_CLIENTS_SUCCESS';
export const UPDATE_CLIENT_FAILURE = 'UPDATE_CLIENT_FAILURE';
export const UPDATE_ASSIGNED_CLIENTS = 'UPDATE_ASSIGNED_CLIENTS';
export const CLEAR_CLIENTS = 'CLEAR_CLIENTS';
export const DELETE_CLIENTS_REQUEST = 'DELETE_CLIENTS_REQUEST';
export const DELETE_CLIENTS_SUCCESS = 'DELETE_CLIENTS_SUCCESS';
export const DELETE_CLIENTS_FAILED = 'DELETE_CLIENTS_FAILED';
export const ADD_COMPANY_REQUEST = 'ADDING_COMPANY';
export const ADD_COMPANY_SUCCESS = 'ADD_COMPANY_SUCCESS';
export const ADD_COMPANY_FAILURE = 'ADD_COMPANY_FAILURE';
export const REPLACE_OPTIMISTIC_COMPANY = 'REPLACE_OPTIMISTIC_COMPANY';
export const UPDATE_CLIENT_CUSTOM_FIELDS_REQUEST =
  'UPDATE_CLIENT_CUSTOM_FIELDS_REQUEST';
export const UPDATE_COMPANY_REQUEST = 'UPDATE_COMPANY_REQUEST';
export const UPDATE_COMPANY_SUCCESS = 'UPDATE_COMPANY_SUCCESS';
export const UPDATE_COMPANIES_SUCCESS = 'UPDATE_COMPANIES_SUCCESS';
export const UPDATE_COMPANY_FAILURE = 'UPDATE_COMPANY_FAILURE';
export const RESET_UPDATED_CLIENT = 'RESET_UPDATED_CLIENT';
export const DELETE_COMPANY_REQUEST = 'DELETE_COMPANY_REQUEST';
export const DELETE_COMPANIES_SUCCESS = 'DELETE_COMPANIES_SUCCESS';
export const DELETE_COMPANY_FAILED = 'DELETE_COMPANY_FAILED';
export const UPDATE_CRM_TABLE_COLUMN_SETTINGS =
  'UPDATE_CRM_TABLE_COLUMN_SETTINGS';

export const ADD_COMPANY_TO_CLIENTS = 'ADD_COMPANY_TO_CLIENTS';
export const REMOVE_COMPANY_FROM_CLIENTS = 'REMOVE_COMPANY_FROM_CLIENTS';

export const SET_CRM_LAST_VISITED_TAB = 'SET_CRM_LAST_VISITED_TAB';

export interface ClearClientsAction {
  type: typeof CLEAR_CLIENTS;
}

export interface LoadClientsDoneAction {
  type: typeof LOAD_CLIENTS_SUCCESS;
  clients: Client[];
  activeClients: User[];
}

export interface StartLoadCompaniesAction {
  type: typeof LOAD_COMPANIES_REQUEST;
}

export interface LoadCompaniesDoneAction {
  type: typeof LOAD_COMPANIES_SUCCESS;
  payload: Company[];
}

export interface LoadCompaniesFailAction {
  type: typeof LOAD_COMPANIES_FAILURE;
  error: string;
}

export interface StartAddClientAction {
  type: typeof ADD_CLIENT_REQUEST;
}

export interface AddClientDoneAction {
  type: typeof ADD_CLIENT_SUCCESS;
  payload: any;
  isWsEvent?: boolean;
}

export interface AddClientFailAction {
  type: typeof ADD_CLIENT_FAILURE;
  error: string;
}

export interface StartInviteClientAction {
  type: typeof INVITE_CLIENT_REQUEST;
}

export interface InviteClientDoneAction {
  type: typeof INVITE_CLIENT_SUCCESS;
  invitedUserId: string;
  invitedByUserId: string;
}

export interface InviteClientFailAction {
  type: typeof INVITE_CLIENT_FAILURE;
  error: string;
}

export interface StartUpdateClientAction {
  type: typeof UPDATE_CLIENT_REQUEST;
  updatingClientId: string;
}

export interface UpdateClientDoneAction {
  type: typeof UPDATE_CLIENTS_SUCCESS;
  isWsEvent?: boolean;
  payload: any;
}

export interface UpdateClientAssigneesActionPayload {
  assignedCompanies: string[];
  clients: User[];
}

export interface UpdateClientAssigneesAction {
  type: typeof UPDATE_ASSIGNED_CLIENTS;
  payload: UpdateClientAssigneesActionPayload;
}

export interface UpdateClientFailAction {
  type: typeof UPDATE_CLIENT_FAILURE;
  error: string;
  updatingClientId: string;
}

export interface StartDeleteClientAction {
  type: typeof DELETE_CLIENTS_REQUEST;
}

export interface DeleteClientDoneAction {
  type: typeof DELETE_CLIENTS_SUCCESS;
  payload: string;
  isWsEvent?: boolean;
}

export interface DeleteClientFailAction {
  type: typeof DELETE_CLIENTS_FAILED;
  error: string;
}

export interface StartAddCompanyAction {
  type: typeof ADD_COMPANY_REQUEST;
}

export interface AddCompanyDoneAction {
  type: typeof ADD_COMPANY_SUCCESS;
  payload: Company[];
}

export interface AddCompanyFailAction {
  type: typeof ADD_COMPANY_FAILURE;
  error: string;
}
export interface ReplaceOptimisticCompanyAction {
  type: typeof REPLACE_OPTIMISTIC_COMPANY;
  payload: {
    optimisticCompanyId: string;
    realCompany: Company;
  };
}

export interface StartUpdateCompanyAction {
  type: typeof UPDATE_COMPANY_REQUEST;
}

export interface UpdateCompanyDoneAction {
  type: typeof UPDATE_COMPANY_SUCCESS;
  payload: any;
}

export interface UpdatedCompaniesDoneAction {
  type: typeof UPDATE_COMPANIES_SUCCESS;
  payload: any;
  isWsEvent?: boolean;
}

export interface ResetUpdatedClientAction {
  type: typeof RESET_UPDATED_CLIENT;
}

export interface UpdateCompanyFailAction {
  type: typeof UPDATE_COMPANY_FAILURE;
  error: string;
}

export interface StartDeleteCompanyAction {
  type: typeof DELETE_COMPANY_REQUEST;
}

export interface DeleteCompanyDoneAction {
  type: typeof DELETE_COMPANIES_SUCCESS;
  payload: string[];
  removeFromAllCompanies?: boolean;
}

export interface DeleteCompanyFailAction {
  type: typeof DELETE_COMPANY_FAILED;
  error: string;
}

export interface UpdateCrmTableColumnSettingsActionType {
  type: typeof UPDATE_CRM_TABLE_COLUMN_SETTINGS;
  columnSettings: Record<string, ColumnSettingsType>;
}

export interface SetCrmLastVisitedTabActionType {
  type: typeof SET_CRM_LAST_VISITED_TAB;
  payload: string;
}

export interface SetCrmLastVisitedTab {
  type: typeof SET_CRM_LAST_VISITED_TAB;
  payload: string;
}

export interface AddCompanyToClientsAction {
  type: typeof ADD_COMPANY_TO_CLIENTS;
  payload: {
    clientIds: string[];
    companyId: string;
  };
}

export interface RemoveCompanyFromClientsAction {
  type: typeof REMOVE_COMPANY_FROM_CLIENTS;
  payload: {
    clientIds: string[];
    companyId: string;
  };
}

export type ClientsActionTypes =
  | ClearClientsAction
  | LoadClientsDoneAction
  | StartLoadCompaniesAction
  | LoadCompaniesDoneAction
  | LoadCompaniesFailAction
  | StartAddClientAction
  | AddClientDoneAction
  | AddClientFailAction
  | StartInviteClientAction
  | InviteClientDoneAction
  | InviteClientFailAction
  | StartDeleteClientAction
  | DeleteClientDoneAction
  | DeleteClientFailAction
  | StartDeleteCompanyAction
  | DeleteCompanyDoneAction
  | DeleteCompanyFailAction
  | StartAddCompanyAction
  | AddCompanyDoneAction
  | AddCompanyFailAction
  | ReplaceOptimisticCompanyAction
  | StartUpdateClientAction
  | UpdateClientDoneAction
  | UpdateClientAssigneesAction
  | UpdateClientFailAction
  | StartUpdateCompanyAction
  | UpdateCompanyDoneAction
  | UpdatedCompaniesDoneAction
  | UpdateCompanyFailAction
  | UpdateCrmTableColumnSettingsActionType
  | SetCrmLastVisitedTabActionType
  | AddCompanyToClientsAction
  | RemoveCompanyFromClientsAction
  | ResetUpdatedClientAction;

export const selectClientsMap = createSelector(
  [(state: RootState) => state.clients.activeClients],
  (activeClients) => arrayToMap(activeClients, 'id'),
);

export const selectShowOnboardingCallout = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    (_, newClients: Client[] | null) => newClients,
    selectCurrentUser,
  ],
  (clients, newClients, currentUser) => {
    return clients.every((client) => {
      if (client.fields.email === currentUser?.fields.email) {
        return false;
      }

      if (client.fields.isTestUser) {
        return true;
      }

      if (newClients === null) {
        return false;
      }

      return newClients.map(({ id }) => id).includes(client.id);
    });
  },
);

export const selectActiveCompaniesMap = createSelector(
  [(state: RootState) => state.clients.activeCompanies],
  (companies) => arrayToMap(companies, 'id'),
);

export const selectAllCompaniesMap = createSelector(
  [(state: RootState) => state.clients.companies],
  (companies) => arrayToMap(companies, 'id'),
);
